import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useFormations } from '@/hooks/useFormations';
import { useAuth } from '@/hooks/useAuth';
import { AlertCircle, CheckCircle, CheckCircle2, DollarSign, Info, Link, Loader2, PartyPopper, RefreshCw, Search, XCircle, Zap } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface LogEntry {
  timestamp: string;
  type: 'info' | 'success' | 'error' | 'warning';
  message: string;
  details?: any;
}

export default function TestPayment() {
  const { toast } = useToast();
  const { loading: authLoading, user } = useAuth();
  const { formations, loading: loadingFormations, error: formationsError, refreshFormations } = useFormations({ silent: true });
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [testData, setTestData] = useState({
    courseId: '',
    formationTitle: '',
    amount: 0,
    currency: 'eur'
  });

  useEffect(() => {
  }, []);

  // Auto-activate advanced mode if not authenticated or if formations error
  useEffect(() => {
    if ((!user && !authLoading && !loadingFormations) || (formationsError && !advancedMode)) {
      setAdvancedMode(true);
    }
  }, [user, authLoading, loadingFormations, formationsError, advancedMode]);

  const handleCourseSelect = (courseId: string) => {
    const selectedCourse = formations.find(f => f.id === courseId);
    if (selectedCourse) {
      setTestData({
        courseId: selectedCourse.id,
        formationTitle: selectedCourse.title,
        amount: selectedCourse.price,
        currency: 'eur'
      });
      addLog('info', `<CheckCircle className="h-3 w-3 inline mr-1" /> Cours sélectionné: ${selectedCourse.title}`, {
        id: selectedCourse.id,
        price: selectedCourse.price
      });
    }
  };

  const isValidUUID = (uuid: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const addLog = (type: LogEntry['type'], message: string, details?: any) => {
    const log: LogEntry = {
      timestamp: new Date().toLocaleTimeString('fr-FR'),
      type,
      message,
      details
    };
    setLogs(prev => [...prev, log]);
  };

  const testStripeCheckout = async () => {
    // Validation avant de commencer
    if (!testData.courseId) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner un cours',
        variant: 'destructive'
      });
      return;
    }

    if (!isValidUUID(testData.courseId)) {
      toast({
        title: 'Erreur',
        description: 'L\'ID du cours doit être un UUID valide',
        variant: 'destructive'
      });
      addLog('error', '<XCircle className="h-3 w-3 inline mr-1" /> UUID invalide', { courseId: testData.courseId });
      return;
    }

    if (testData.amount <= 0) {
      toast({
        title: 'Erreur',
        description: 'Le montant doit être supérieur à 0',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    setLogs([]);
    
    try {
      addLog('info', '<Search className="h-3 w-3 inline mr-1" /> Vérification de l\'authentification...');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        addLog('error', '<XCircle className="h-3 w-3 inline mr-1" /> Utilisateur non authentifié');
        toast({
          title: 'Erreur',
          description: 'Vous devez être connecté pour tester le paiement',
          variant: 'destructive'
        });
        return;
      }
      
      addLog('success', `<CheckCircle className="h-3 w-3 inline mr-1" /> Utilisateur authentifié: ${user.email}`, { userId: user.id });

      addLog('info', '💳 Préparation du paiement test...', testData);

      const amountInCents = Math.round(testData.amount * 100);
      addLog('info', `<DollarSign className="h-3 w-3 inline mr-1" /> Montant: ${testData.amount}€ (${amountInCents} centimes)`);

      addLog('info', '🚀 Appel de la fonction create-stripe-checkout...');
      const { data, error } = await supabase.functions.invoke('create-stripe-checkout', {
        body: {
          courseId: testData.courseId,
          formationTitle: testData.formationTitle,
          amount: amountInCents,
          currency: testData.currency.toLowerCase(),
          inscriptionId: 'test-inscription-' + Date.now(),
          registrationProgressId: null,
          promoCodeId: null,
          discountApplied: 0,
          originalAmount: amountInCents
        }
      });

      if (error) {
        addLog('error', '<XCircle className="h-3 w-3 inline mr-1" /> Erreur lors de l\'appel de la fonction', {
          name: error.name,
          message: error.message,
          context: error.context
        });
        throw new Error(error.message || 'Erreur lors de l\'appel de la fonction');
      }

      addLog('success', '<CheckCircle className="h-3 w-3 inline mr-1" /> Fonction appelée avec succès', data);

      if (data?.url) {
        addLog('success', '<PartyPopper className="h-3 w-3 inline mr-1" /> Session Stripe créée avec succès!', {
          sessionId: data.sessionId,
          paymentId: data.paymentId
        });
        
        addLog('info', '<Link className="h-3 w-3 inline mr-1" /> Redirection vers Stripe Checkout...');
        
        toast({
          title: 'Succès!',
          description: 'Redirection vers Stripe en cours...'
        });

        setTimeout(() => {
          window.location.href = data.url;
        }, 2000);
      } else {
        addLog('error', '<XCircle className="h-3 w-3 inline mr-1" /> Aucune URL de session retournée', data);
        throw new Error('Aucune URL de paiement retournée');
      }

    } catch (error: any) {
      console.error('Test payment error:', error);
      addLog('error', '💥 Erreur critique', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      toast({
        title: 'Erreur de paiement',
        description: error.message || 'Erreur lors du test de paiement',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Zap className="w-4 h-4 text-blue-500" />;
    }
  };

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      {/* Debug Banner */}
      <Card className="mb-6 border-blue-500/50 bg-blue-50/50 dark:bg-blue-950/50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">Auth:</span>
                {authLoading ? (
                  <span className="flex items-center gap-1 text-orange-600">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Chargement...
                  </span>
                ) : user ? (
                  <span className="text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {user.email}
                  </span>
                ) : (
                  <span className="text-red-600">Non connecté</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">Formations:</span>
                {loadingFormations ? (
                  <span className="flex items-center gap-1 text-orange-600">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Chargement...
                  </span>
                ) : formationsError ? (
                  <span className="text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Erreur (mode avancé activé)
                  </span>
                ) : (
                  <span className="text-green-600">{formations.length} disponibles</span>
                )}
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => refreshFormations()}
              disabled={loadingFormations}
              className="gap-2"
            >
              <RefreshCw className={`h-3 w-3 ${loadingFormations ? 'animate-spin' : ''}`} />
              Recharger formations
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alert si erreur formations / non connecté */}
      {formationsError && (
        <Alert className="mb-6 border-orange-500 bg-orange-50 dark:bg-orange-950">
          <Info className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-3">
              <p>
                <strong>Formations non disponibles</strong> - Vous n'êtes pas connecté. 
                Utilisez le <strong>Mode Avancé</strong> ci-dessous pour saisir manuellement les détails, 
                ou connectez-vous pour accéder à la liste complète.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = '/auth'}
                >
                  Se connecter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('https://supabase.com/dashboard/project/vsstvjkmkmqejrvxklhn/functions/get-formations/logs', '_blank')}
                >
                  Voir logs get-formations
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Test de Paiement Stripe</h1>
        <p className="text-muted-foreground">
          Testez le flux de paiement complet avec logs détaillés
        </p>
      </div>

      <Alert className="mb-6">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Cette page est réservée aux tests. Utilisez une carte test Stripe: <strong>4242 4242 4242 4242</strong>
        </AlertDescription>
      </Alert>

      <div className="grid gap-6">
        {/* Debug Webhooks Stripe */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Debug Webhooks Stripe
            </CardTitle>
            <CardDescription>
              Vérifiez que Stripe communique bien avec vos webhooks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
              <h4 className="font-semibold text-blue-900 dark:text-blue-100">Étapes de test</h4>
              <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-2 list-decimal list-inside">
                <li>Configurez temporairement le webhook vers <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">webhook-debug</code></li>
                <li>Envoyez un événement test <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">checkout.session.completed</code> depuis Stripe</li>
                <li>Vérifiez les logs ci-dessous pour confirmer la réception (200 OK)</li>
                <li>Si OK, remettez l'endpoint sur <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">stripe-webhook</code> et retestez</li>
              </ol>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Button
                variant="outline"
                className="gap-2 justify-start"
                onClick={() => window.open('https://supabase.com/dashboard/project/vsstvjkmkmqejrvxklhn/functions/stripe-webhook/logs', '_blank')}
              >
                <CheckCircle2 className="h-4 w-4" />
                Logs stripe-webhook
              </Button>
              
              <Button
                variant="outline"
                className="gap-2 justify-start"
                onClick={() => window.open('https://supabase.com/dashboard/project/vsstvjkmkmqejrvxklhn/functions/webhook-debug/logs', '_blank')}
              >
                <Info className="h-4 w-4" />
                Logs webhook-debug
              </Button>
              
              <Button
                variant="outline"
                className="gap-2 justify-start"
                onClick={() => window.open('https://dashboard.stripe.com/test/webhooks', '_blank')}
              >
                <Zap className="h-4 w-4" />
                Stripe Webhooks
              </Button>
            </div>
            
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>webhook-debug</strong> : Endpoint temporaire qui log tout et répond 200 OK. Utilisez-le pour valider la connectivité.
              </p>
            </div>
          </CardContent>
        </Card>
        {/* Configuration du test */}
        <Card>
          <CardHeader>
            <CardTitle>Configuration du Test</CardTitle>
            <CardDescription>
              Sélectionnez un cours réel pour tester le paiement
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Toggle Mode Avancé */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="space-y-0.5">
                <Label htmlFor="advanced-mode" className="text-sm font-medium">
                  Mode Avancé
                </Label>
                <p className="text-xs text-muted-foreground">
                  Saisie manuelle des paramètres (peut échouer si UUID invalide)
                </p>
              </div>
              <Switch
                id="advanced-mode"
                checked={advancedMode}
                onCheckedChange={setAdvancedMode}
              />
            </div>

            {!advancedMode ? (
              <>
                {/* Sélecteur de cours */}
                <div className="space-y-2">
                  <Label htmlFor="courseSelect">Sélectionner un cours</Label>
                  {loadingFormations ? (
                    <div className="flex items-center justify-center p-4 border rounded-md">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">Chargement des cours...</span>
                    </div>
                  ) : formations.length === 0 ? (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Aucun cours disponible. Activez le mode avancé pour saisir manuellement.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Select value={testData.courseId} onValueChange={handleCourseSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choisissez un cours..." />
                      </SelectTrigger>
                      <SelectContent>
                        {formations.map((formation) => (
                          <SelectItem key={formation.id} value={formation.id}>
                            {formation.title} - {formation.price}€
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Affichage des détails du cours sélectionné */}
                {testData.courseId && (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-1">
                        <p className="font-medium">{testData.formationTitle}</p>
                        <p className="text-xs font-mono text-muted-foreground">UUID: {testData.courseId}</p>
                        <p className="text-sm">Prix: {testData.amount}€</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </>
            ) : (
              <>
                {/* Mode manuel avancé */}
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Mode avancé: L'UUID doit être valide et exister dans la base de données
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="courseId">UUID du cours</Label>
                  <Input
                    id="courseId"
                    value={testData.courseId}
                    onChange={(e) => setTestData({ ...testData, courseId: e.target.value })}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className={!isValidUUID(testData.courseId) && testData.courseId ? 'border-destructive' : ''}
                  />
                  {testData.courseId && !isValidUUID(testData.courseId) && (
                    <p className="text-xs text-destructive">UUID invalide</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="formationTitle">Titre de la formation</Label>
                  <Input
                    id="formationTitle"
                    value={testData.formationTitle}
                    onChange={(e) => setTestData({ ...testData, formationTitle: e.target.value })}
                    placeholder="Formation Test Stripe"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount">Montant (€)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={testData.amount}
                      onChange={(e) => setTestData({ ...testData, amount: parseFloat(e.target.value) || 0 })}
                      placeholder="99.90"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="currency">Devise</Label>
                    <Input
                      id="currency"
                      value={testData.currency}
                      onChange={(e) => setTestData({ ...testData, currency: e.target.value })}
                      placeholder="eur"
                    />
                  </div>
                </div>
              </>
            )}

            <Button 
              onClick={testStripeCheckout} 
              disabled={loading || !testData.courseId || (advancedMode && !isValidUUID(testData.courseId))}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Test en cours...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-4 w-4" />
                  Lancer le Test de Paiement
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Logs en temps réel */}
        {logs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Logs en Temps Réel</CardTitle>
              <CardDescription>
                Suivez le déroulement du test étape par étape
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 font-mono text-sm">
                {logs.map((log, index) => (
                  <div 
                    key={index}
                    className={`flex items-start gap-2 p-2 rounded ${
                      log.type === 'error' ? 'bg-red-50 dark:bg-red-950' :
                      log.type === 'success' ? 'bg-green-50 dark:bg-green-950' :
                      log.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-950' :
                      'bg-blue-50 dark:bg-blue-950'
                    }`}
                  >
                    {getLogIcon(log.type)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{log.timestamp}</span>
                        <span>{log.message}</span>
                      </div>
                      {log.details && (
                        <pre className="mt-1 text-xs overflow-x-auto text-muted-foreground">
                          {JSON.stringify(log.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle>Instructions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                1
              </div>
              <p className="text-sm">Vérifiez que vous êtes connecté avec un compte utilisateur</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                2
              </div>
              <p className="text-sm">Cliquez sur "Lancer le Test de Paiement" pour créer une session Stripe</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                3
              </div>
              <p className="text-sm">Suivez les logs pour voir chaque étape du processus</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                4
              </div>
              <p className="text-sm">Utilisez la carte test <strong>4242 4242 4242 4242</strong> dans Stripe Checkout</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0">
                5
              </div>
              <p className="text-sm">Vérifiez dans le Dashboard Stripe que le paiement est bien enregistré</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
